/**
 * registry.js — Component, Slot, and Feature Registry
 *
 * PURPOSE:
 *   Central registry for UI components, slot injections, and
 *   optional features. Allows winery variants to:
 *     - Override a base component (registerComponent)
 *     - Inject extra sections at named anchors (registerSlot)
 *     - Ship optional features gated by config (registerFeature)
 *
 * USAGE:
 *   import { registry } from './core/registry.js';
 *   registry.registerComponent('Header', MyHeader);
 *   registry.registerSlot('after-banner', LoyaltyStrip);
 *   const Comp = registry.getComponent('Header');
 *   const slots = registry.getSlots('after-banner');
 */

const components = new Map();
const slots = new Map();

export const registry = {
  // ─── COMPONENTS ────────────────────────────────────────
  registerComponent(name, component) {
    components.set(name, component);
  },

  getComponent(name) {
    return components.has(name) ? components.get(name) : null;
  },

  hasComponent(name) {
    return components.has(name);
  },

  // ─── SLOTS ─────────────────────────────────────────────
  // Multiple components per anchor, rendered in registration order.
  registerSlot(anchor, component) {
    if (!slots.has(anchor)) slots.set(anchor, []);
    slots.get(anchor).push(component);
  },

  getSlots(anchor) {
    return slots.get(anchor) || [];
  },

  // ─── FEATURES ──────────────────────────────────────────
  features: new Map(),

  registerFeature(name, featureModule) {
    this.features.set(name, featureModule);
  },

  async initFeatures(tenantFeatures) {
    for (const [name, featureModule] of this.features) {
      if (tenantFeatures[name] === true) {
        try {
          await featureModule.init();
          console.info(`[RedConcierge] Feature initialized: ${name}`);
        } catch (err) {
          console.error(
            `[RedConcierge] Feature failed to initialize: ${name}`,
            err
          );
        }
      }
    }
  },
};
